import React, { FC } from 'react';
import RadioIcon, { OptionButton } from '~Components/RadioOptions';
import { useStorage } from '~Components/Storage';
import Heading from '~ComponentsImproved/Typography/Heading';
import { useButton, useDispatch, useI18n } from '~Hooks';
import { Box } from '~Styles';
import { GetDevices_operativeViewer_devices } from '~Components/DeviceProvider/__queries__';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import Icon from '~ComponentsImproved/Icon';
import LockClockIcon from '~Assets/icons/lock_clock.svg';
import { useDeviceHandler } from '~Components/DeviceProvider';
import { DateTime } from 'luxon';
import { PRODUCT_TO_UI_COPY_MAP } from '~Providers/LicensesProvider/Components/ClaimLicense';
import * as actions from '~actions';

interface Props {
  device: GetDevices_operativeViewer_devices;
  isSelected: boolean;
  onSelect: (id: string) => void;
}

/** A device that can be selected to claim (if the device is already used by the same clientId it will be disabled) */
const SelectableDevice: FC<Props> = ({ device, isSelected, onSelect }) => {
  const { i18n } = useI18n();

  const [storage] = useStorage();
  const deviceHandler = useDeviceHandler();
  const dispatch = useDispatch();

  /** Allow selection of all devices if isUsingBrowser is true */
  const isUsingBrowser = !window.ReactNativeWebView;

  const selectButton = useButton({
    onClick: () => {
      if (isUsedByThisDevice) {
        return;
      }

      if (device?.lastClaimedAt && hasClaimCooldown && !isUsingBrowser) {
        // Create a DateTime object directly from the lastClaimedAt ISO string
        const cooldownEndDate = DateTime.fromISO(device.lastClaimedAt, { zone: 'utc' })
          .plus({ days: 7 })
          .toFormat('d MMMM HH:mm');

        dispatch(
          actions.addToast(
            `${i18n('ClaimDevice.Select.DeviceInUse', device?.name)} ${cooldownEndDate}`,
            true,
          ),
        );
        return;
      }

      onSelect(device.id);
    },
  });

  const activeLicenses = device?.activeLicenses || [];

  /** Don't display devices if this is true since then it can be an old company with the old licenses setup */
  const hasAnyLicenseOnAnyDevice = deviceHandler.allDevices.some(
    (d) => d.activeLicenses && d.activeLicenses.length > 0,
  );

  const hasClaimCooldown = device?.lastClaimedAt
    ? DateTime.fromISO(device.lastClaimedAt, { zone: 'utc' })
        .plus({ days: 7 })
        .toJSDate() > DateTime.utc().toJSDate()
    : false;

  const isUsedByThisDevice = device?.id === storage.deviceId;

  return (
    <OptionButton
      selected={isSelected}
      {...selectButton}
      disabled={!isUsingBrowser && (hasClaimCooldown || isUsedByThisDevice)}>
      <RadioIcon active={isSelected} />
      <Box display="flex" alignItems="center" justifyContent="space-between" fluid>
        <Box display="flex" flexDirection="column">
          <Heading level={4} bottom={0} color="var(--colorWhite)">
            {device.name}{' '}
            {device?.ownerClientId &&
              `(${
                isUsedByThisDevice
                  ? i18n('ClaimDevice.Select.InUseByThisDevice')
                  : i18n('ClaimDevice.Select.InUse')
              })`}
          </Heading>
          {hasAnyLicenseOnAnyDevice && (
            <Paragraph level={5} bottom={0} color="var(--colorBlueGray200)">
              {i18n('ClaimDevice.Select.Licenses')}:{' '}
              {activeLicenses.length > 0
                ? [...activeLicenses]
                    .sort((a, b) =>
                      (PRODUCT_TO_UI_COPY_MAP[a.product] || '').localeCompare(
                        PRODUCT_TO_UI_COPY_MAP[b.product] || '',
                      ),
                    )
                    .map(
                      (license, index, sortedArray) =>
                        `${PRODUCT_TO_UI_COPY_MAP[license.product]}${
                          index < sortedArray.length - 1 ? ', ' : ''
                        }`,
                    )
                : i18n('ClaimDevice.Select.Licenses.None')}
            </Paragraph>
          )}
        </Box>

        {hasClaimCooldown && !isUsedByThisDevice && (
          <Icon height="24px" width="24px" src={LockClockIcon} color="var(--colorBlueGray200)" />
        )}
      </Box>
    </OptionButton>
  );
};

export default SelectableDevice;
